import { useEffect, useCallback, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  ArcElement,
  Legend
} from 'chart.js';
import { Doughnut, Bar } from "react-chartjs-2";

import izenuAPI from '../../services/izenuAPI';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  ArcElement,
  Legend
);

const CDN = 'https://dpagz47n4g1lw.cloudfront.net';

const GENRE_COLORS = [
  '#3992ff', '#ff6b6b', '#ffd93d', '#6bcb77',
  '#4d96ff', '#ff922b', '#cc5de8', '#20c997'
];

const COUNTRY_COLORS = [
  '#3992ff', '#ff6b6b', '#ffd93d', '#6bcb77',
  '#4d96ff', '#ff922b', '#cc5de8', '#20c997'
];

function formatNumber(num) {
  if (!num) return '0';
  const n = Number(num);
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
  return n.toLocaleString('pt-PT');
}

function formatEarnings(num) {
  if (!num) return '0.00';
  return Number(num).toFixed(2);
}

function getCoverUrl(artistId, cover) {
  if (!cover) return null;
  if (cover.startsWith('http')) return cover;
  if (cover.startsWith('release_')) return `${CDN}/users/${artistId}/releases/${cover}`;
  return `${CDN}/songs/artworks/${cover}`;
}

function getArtistPhoto(userId, photo) {
  if (!photo) return null;
  if (photo.startsWith('http')) return photo;
  return `${CDN}/users/${userId}/${photo}`;
}

// Stat card component
function StatCard({ label, value, sublabel, color = '#3992ff', icon }) {
  return (
    <div style={styles.statCard}>
      <div style={{ ...styles.statIcon, backgroundColor: color + '18', color }}>
        {icon}
      </div>
      <div style={{ flex: 1 }}>
        <p style={styles.statLabel}>{label}</p>
        <h2 style={{ ...styles.statValue, color }}>{value}</h2>
        {sublabel && <p style={styles.statSublabel}>{sublabel}</p>}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [dashData, setDashData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = useCallback(async () => {
    try {
      const end_point = '/distributor/fetchDashboardData.php';
      const { data: response } = await izenuAPI.get(end_point);
      setDashData(response);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <p style={{ color: '#888' }}>A carregar dashboard...</p>
      </div>
    );
  }

  if (!dashData) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <p style={{ color: '#888' }}>Erro ao carregar dados.</p>
      </div>
    );
  }

  // Chart data for genres (doughnut)
  const genreChartData = {
    labels: (dashData.streams_by_genre || []).map(g => g.genre),
    datasets: [{
      data: (dashData.streams_by_genre || []).map(g => Number(g.streams)),
      backgroundColor: GENRE_COLORS,
      borderWidth: 0,
      hoverOffset: 6
    }]
  };

  const genreChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '65%',
    plugins: {
      legend: {
        position: 'right',
        labels: { boxWidth: 12, padding: 12, font: { size: 12 }, color: '#555' }
      }
    }
  };

  // Chart data for countries (horizontal bar)
  const countryChartData = {
    labels: (dashData.streams_by_country || []).map(c => c.country),
    datasets: [{
      data: (dashData.streams_by_country || []).map(c => Number(c.streams)),
      backgroundColor: COUNTRY_COLORS.slice(0, (dashData.streams_by_country || []).length),
      borderRadius: 4,
      barThickness: 22
    }]
  };

  const countryChartOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 11 }, color: '#888' }
      },
      y: {
        grid: { display: false },
        ticks: { font: { size: 12 }, color: '#333' }
      }
    }
  };

  const releases = dashData.releases || {};

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Dashboard</h1>
          <p style={styles.subtitle}>Dados da distribuidora — {localStorage.getItem('name') || ''}</p>
        </div>
      </div>

      {/* Stats Row */}
      <div style={styles.statsRow}>
        <StatCard
          label="Artistas"
          value={formatNumber(dashData.artists)}
          icon="👤"
          color="#3992ff"
        />
        <StatCard
          label="Músicas"
          value={formatNumber(dashData.songs)}
          sublabel={dashData.recent_uploads > 0 ? `+${dashData.recent_uploads} últimos 30 dias` : null}
          icon="🎵"
          color="#6bcb77"
        />
        <StatCard
          label="Streams"
          value={formatNumber(dashData.total)}
          icon="▶"
          color="#ff922b"
        />
        <StatCard
          label="Receita"
          value={`${formatEarnings(dashData.earnings)} Kz`}
          icon="💰"
          color="#cc5de8"
        />
      </div>

      {/* Secondary stats */}
      <div style={styles.statsRow}>
        <StatCard
          label="Álbuns e singles"
          value={formatNumber(releases.total)}
          sublabel={`${releases.singles || 0} singles · ${releases.eps || 0} EPs · ${releases.albums || 0} álbuns`}
          icon="💿"
          color="#4d96ff"
        />
        <StatCard
          label="Playlists"
          value={formatNumber(dashData.playlists)}
          icon="📋"
          color="#20c997"
        />
      </div>

      {/* Charts row */}
      <div style={styles.chartsRow}>
        {/* Streams by Genre */}
        <div style={styles.chartCard}>
          <h3 style={styles.sectionTitle}>Streams por Género</h3>
          <p style={styles.sectionDesc}>Distribuição dos streams qualificados (30s+) por género musical dos seus artistas.</p>
          {(dashData.streams_by_genre || []).length > 0 ? (
            <div style={{ height: 220 }}>
              <Doughnut data={genreChartData} options={genreChartOptions} />
            </div>
          ) : (
            <p style={styles.emptyText}>Sem dados de géneros</p>
          )}
        </div>

        {/* Streams by Country */}
        <div style={styles.chartCard}>
          <h3 style={styles.sectionTitle}>Streams por País</h3>
          <p style={styles.sectionDesc}>Países de onde vêm os ouvintes dos seus artistas, baseado na geolocalização.</p>
          {(dashData.streams_by_country || []).length > 0 ? (
            <div style={{ height: 220 }}>
              <Bar data={countryChartData} options={countryChartOptions} />
            </div>
          ) : (
            <p style={styles.emptyText}>Sem dados de países</p>
          )}
        </div>
      </div>

      {/* Lists row */}
      <div style={styles.chartsRow}>
        {/* Top Songs */}
        <div style={styles.chartCard}>
          <h3 style={styles.sectionTitle}>Top Músicas</h3>
          <p style={styles.sectionDesc}>As 5 músicas com mais streams qualificados dos artistas da distribuidora.</p>
          {(dashData.top_songs || []).length > 0 ? (
            <div>
              {dashData.top_songs.map((song, index) => (
                <div key={song.music_id} style={styles.listItem}>
                  <span style={styles.rank}>#{index + 1}</span>
                  <img
                    src={getCoverUrl(song.artist_id, song.cover) || '/placeholder.png'}
                    alt=""
                    style={styles.coverImg}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={styles.songTitle}>{song.title}</p>
                    <p style={styles.songArtist}>{song.artist_name}</p>
                  </div>
                  <span style={styles.streamCount}>{formatNumber(song.streams)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p style={styles.emptyText}>Sem dados de streams</p>
          )}
        </div>

        {/* Top Artists */}
        <div style={styles.chartCard}>
          <h3 style={styles.sectionTitle}>Top Artistas</h3>
          <p style={styles.sectionDesc}>Os 5 artistas com maior volume de streams na plataforma.</p>
          {(dashData.top_artists || []).length > 0 ? (
            <div>
              {dashData.top_artists.map((artist, index) => (
                <div key={artist.user_id} style={styles.listItem}>
                  <span style={styles.rank}>#{index + 1}</span>
                  <img
                    src={getArtistPhoto(artist.user_id, artist.photo) || '/placeholder.png'}
                    alt=""
                    style={styles.artistImg}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={styles.songTitle}>{artist.artist_name}</p>
                    <p style={styles.songArtist}>{artist.number_of_followers || 0} seguidores</p>
                  </div>
                  <span style={styles.streamCount}>{formatNumber(artist.streams)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p style={styles.emptyText}>Sem dados de artistas</p>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    padding: '0 10px 40px 20px',
    maxWidth: 1200,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 24,
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    color: '#1a1a2e',
    margin: 0,
  },
  subtitle: {
    margin: '4px 0 0 0',
    fontSize: 13,
    color: '#999',
    fontWeight: 400,
  },
  statsRow: {
    display: 'flex',
    gap: 16,
    marginBottom: 20,
    flexWrap: 'wrap',
  },
  statCard: {
    flex: '1 1 220px',
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    padding: '20px 22px',
    backgroundColor: '#fff',
    borderRadius: 12,
    boxShadow: '0 1px 8px rgba(0,0,0,0.06)',
    border: '1px solid #f0f0f0',
    minWidth: 200,
  },
  statIcon: {
    width: 44,
    height: 44,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 20,
    flexShrink: 0,
  },
  statLabel: {
    margin: 0,
    fontSize: 13,
    color: '#888',
    fontWeight: 500,
  },
  statValue: {
    margin: '2px 0',
    fontSize: 26,
    fontWeight: 700,
    lineHeight: 1.2,
  },
  statSublabel: {
    margin: 0,
    fontSize: 11,
    color: '#aaa',
  },
  chartsRow: {
    display: 'flex',
    gap: 16,
    marginBottom: 20,
    flexWrap: 'wrap',
  },
  chartCard: {
    flex: '1 1 400px',
    padding: 24,
    backgroundColor: '#fff',
    borderRadius: 12,
    boxShadow: '0 1px 8px rgba(0,0,0,0.06)',
    border: '1px solid #f0f0f0',
    minWidth: 320,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: '#333',
    margin: '0 0 4px 0',
  },
  sectionDesc: {
    margin: '0 0 14px 0',
    fontSize: 12,
    color: '#aaa',
    fontWeight: 400,
    lineHeight: 1.4,
  },
  listItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 0',
    borderBottom: '1px solid #f5f5f5',
  },
  rank: {
    fontSize: 13,
    fontWeight: 700,
    color: '#bbb',
    width: 26,
    textAlign: 'center',
    flexShrink: 0,
  },
  coverImg: {
    width: 40,
    height: 40,
    borderRadius: 6,
    objectFit: 'cover',
    backgroundColor: '#f0f0f0',
    flexShrink: 0,
  },
  artistImg: {
    width: 40,
    height: 40,
    borderRadius: 20,
    objectFit: 'cover',
    backgroundColor: '#f0f0f0',
    flexShrink: 0,
  },
  songTitle: {
    margin: 0,
    fontSize: 14,
    fontWeight: 600,
    color: '#222',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  songArtist: {
    margin: 0,
    fontSize: 12,
    color: '#999',
  },
  streamCount: {
    fontSize: 14,
    fontWeight: 600,
    color: '#3992ff',
    flexShrink: 0,
  },
  emptyText: {
    color: '#ccc',
    textAlign: 'center',
    padding: 40,
    fontSize: 14,
  },
};
